import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, CardBody, Spinner, Table } from 'reactstrap';
import {
  TelephonyAgent,
  TelephonyCall,
  telephonyUserApi,
} from './telephonyApi';
import RecordingPlayer from './RecordingPlayer';

/**
 * The calls behind the numbers, for an agent looking at their own analytics.
 *
 * Replaces the agent-wise breakdown table on the Analytics page for anyone who
 * is not a telephony manager: a one-row table of yourself tells you nothing,
 * whereas the individual calls are what you would actually act on.
 *
 * Identity comes from GET /v1/agents/me — resolved by the platform from the
 * caller's own CMS token — rather than from the full agent directory. A
 * non-manager has no business enumerating every agent just to find their own
 * id, and the token already knows who they are.
 *
 * Caveat worth keeping in view: GET /v1/calls is NOT scoped server-side. It
 * deliberately shows every CMS user every call (that is what Telephony → Logs
 * is), so the agentId below is the client asking politely, not the server
 * enforcing. This exposes nothing Logs does not already, but it is weaker than
 * the analytics numbers above it, which the platform does enforce.
 */

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: 'success',
  BRIDGED: 'warning',
  IN_PROGRESS: 'warning',
  RINGING: 'warning',
  INITIATED: 'warning',
  QUEUED: 'secondary',
  NO_ANSWER: 'danger',
  BUSY: 'danger',
  AGENT_NOT_CONNECTED: 'danger',
  FAILED: 'danger',
  CANCELED: 'secondary',
  NOT_CONNECTED: 'dark',
};

/** Same wording as the Logs page, so one call never reads two different ways. */
const statusLabel = (call: TelephonyCall): string => {
  if (call.direction === 'INBOUND' && call.status === 'NO_ANSWER') return 'MISSED INCOMING';
  if (call.status === 'NOT_CONNECTED') return 'NOT CONNECTED (VOICEMAIL)';
  return call.status;
};

const DirectionArrow: React.FC<{ direction?: string }> = ({ direction }) => {
  const inbound = direction === 'INBOUND';
  return (
    <i
      className={`fa ${inbound ? 'fa-arrow-down' : 'fa-arrow-up'} me-2`}
      title={inbound ? 'Incoming call' : 'Outgoing call'}
      aria-label={inbound ? 'Incoming call' : 'Outgoing call'}
      style={{
        color: inbound ? '#14b87f' : '#8a84a3',
        fontSize: 11,
        transform: 'rotate(45deg)',
        display: 'inline-block',
      }}
    />
  );
};

const PAGE_SIZE = 25;

const MyCallsList: React.FC<{
  /** Local yyyy-mm-dd, matching the Analytics filter bar. */
  fromDate: string;
  toDate: string;
  direction: string;
}> = ({ fromDate, toDate, direction }) => {
  const navigate = useNavigate();
  const [agent, setAgent] = useState<TelephonyAgent | null>(null);
  const [resolvingAgent, setResolvingAgent] = useState(true);
  const [calls, setCalls] = useState<TelephonyCall[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    telephonyUserApi<{ agent: TelephonyAgent | null }>('/v1/agents/me')
      .then((res) => setAgent(res.agent))
      .catch((e: Error) => setError(e.message))
      .finally(() => setResolvingAgent(false));
  }, []);

  // A filter change invalidates whatever page we were on.
  useEffect(() => {
    setPage(0);
  }, [fromDate, toDate, direction]);

  const load = useCallback(() => {
    if (!agent) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      agentId: agent.id,
      take: String(PAGE_SIZE),
      skip: String(page * PAGE_SIZE),
    });
    // Same local start/end-of-day bounds the analytics request uses, so this
    // list is exactly the calls the cards above counted.
    if (fromDate) params.set('from', new Date(`${fromDate}T00:00:00`).toISOString());
    if (toDate) params.set('to', new Date(`${toDate}T23:59:59.999`).toISOString());
    if (direction) params.set('direction', direction);

    telephonyUserApi<{ items: TelephonyCall[]; total: number }>(`/v1/calls?${params}`)
      .then((res) => {
        setCalls(res.items);
        setTotal(res.total);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [agent, fromDate, toDate, direction, page]);

  useEffect(load, [load]);

  if (resolvingAgent) {
    return (
      <Card>
        <CardBody className="text-center text-muted py-5">
          <Spinner size="sm" className="me-2" />
          Loading your calls…
        </CardBody>
      </Card>
    );
  }

  // Not provisioned as an agent: a normal answer, not an error. The cards above
  // will read zero for the same reason, so say why once, here.
  if (!agent) {
    return (
      <Card>
        <CardBody className="text-center text-muted py-5">
          <div className="mb-2" style={{ fontSize: 26 }}>☎️</div>
          <h6 className="mb-1">You don&apos;t have a telephony agent yet</h6>
          <p className="small mb-0">
            Calls you make will show up here once someone provisions you under
            Telephony → Agents.
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center mb-3">
          <h6 className="mb-0 fw-bold">My calls</h6>
          <span className="text-muted small ms-auto">
            {loading ? (
              <><Spinner size="sm" className="me-1" />Updating…</>
            ) : total > 0 ? (
              `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`
            ) : null}
          </span>
        </div>

        {error ? (
          <div className="text-center py-5">
            <div className="mb-2" style={{ fontSize: 26 }}>⚠️</div>
            <h6 className="mb-1">Couldn&apos;t load your calls</h6>
            <p className="text-muted small mb-3">{error}</p>
            <Button size="sm" color="primary" outline onClick={load}>
              Try again
            </Button>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th className="text-nowrap">Recording</th>
                    <th></th>
                    <th className="text-nowrap">Time</th>
                    <th>Customer</th>
                    <th>Status</th>
                    <th className="text-end">Talk time</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && calls.length === 0 ? (
                    <tr><td colSpan={6} className="text-center text-muted py-4">Loading…</td></tr>
                  ) : calls.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        No calls in this date range.
                        <div className="small mt-1">Try widening the dates.</div>
                      </td>
                    </tr>
                  ) : (
                    calls.map((c) => {
                      const stored = c.recordings.find((r) => r.status === 'STORED');
                      return (
                        <tr key={c.id}>
                          <td>
                            {stored ? (
                              <RecordingPlayer recordingId={stored.id} />
                            ) : c.recordings.length ? (
                              <small className="text-muted">
                                {c.recordings[0].status.toLowerCase()}
                              </small>
                            ) : (
                              <small className="text-muted">—</small>
                            )}
                          </td>
                          <td className="text-nowrap">
                            <Button
                              size="sm"
                              color="secondary"
                              outline
                              onClick={() =>
                                navigate(
                                  `${process.env.PUBLIC_URL}/pages/telephony-logs/${c.id}`,
                                )
                              }
                            >
                              View
                            </Button>
                          </td>
                          <td className="text-nowrap">
                            {new Date(c.createdAt).toLocaleString()}
                          </td>
                          <td className="text-nowrap">
                            <DirectionArrow direction={c.direction} />
                            {c.channel === 'WHATSAPP' ? (
                              <i
                                className="fa fa-whatsapp me-1"
                                title="WhatsApp call"
                                aria-label="WhatsApp call"
                                style={{ color: '#25D366', fontSize: 12 }}
                              />
                            ) : null}
                            {c.customerE164}
                          </td>
                          <td>
                            <Badge color={STATUS_COLORS[c.status] ?? 'secondary'}>
                              {statusLabel(c)}
                            </Badge>
                          </td>
                          <td
                            className="text-end"
                            style={{ fontVariantNumeric: 'tabular-nums' }}
                          >
                            {c.talkSeconds != null && c.talkSeconds > 0
                              ? `${c.talkSeconds}s`
                              : '—'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>

            {total > PAGE_SIZE ? (
              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button
                  size="sm"
                  color="light"
                  disabled={loading || page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  color="light"
                  disabled={loading || (page + 1) * PAGE_SIZE >= total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            ) : null}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default MyCallsList;
